import { getOptimizelyDataFile, createOptimizelyInstance } from './getOptimizelyInstance';
import forceOptimizelyExperiments from './forceOptimizelyExperiments';
const tenSecondsMS = 10000;
const allowedAttempts = 5;
const delayMS = milliseconds => new Promise(res => {
  setTimeout(res, milliseconds);
});

// extract the user's location from the url (/city-state-zip OR /city-state)
const getLocationFromUrl = ctx => {
  const url = ctx.req?.url || '';

  // the location should be the last part of the url path
  const locationSlug = url.split('?')[0].split('/').pop();
  const pattern = /^([A-Za-z\-.']+)(?:-)+([A-Za-z]{2})(?=-)(?=.\d)(?:-)+(\d{5})$/; // city-state-zip
  const nozipPattern = /^([A-Za-z\-.']+)(?:-)+([A-Za-z]{2}$)/; // city-state
  const match = locationSlug.match(pattern) || locationSlug.match(nozipPattern);
  if (match) {
    const [, city, state, zip] = match;
    return {
      city,
      state,
      zip
    };
  }
  return {};
};
const addOptimizelyDynamicConfig = ({
  environment = 'prod',
  urlPath = '',
  onConfigLoadError = () => {}
} = {}) => async ctx => {
  if (typeof window === 'undefined') {
    if (!ctx.data.ATC_ID) {
      // eslint-disable-next-line no-console
      console.error('Missing `ctx.data.ATC_ID`. Make sure addAtcId middleware is present earlier in the middleware queue.');
      return;
    }
    try {
      let remainingAttempts = allowedAttempts;
      while (!ctx.data.optimizelyDynamicConfig) {
        // eslint-disable-next-line no-await-in-loop
        if (await createOptimizelyInstance()) {
          const userId = ctx.data.ATC_ID;
          // This call has a side effect of forcing specific experiment variations but it returns feature flags
          // because directly forcing those is not an Optimizely capability.
          const {
            forcedFeatures,
            forcedExperiments
          } = forceOptimizelyExperiments(ctx, userId);
          const {
            city,
            state,
            zip
          } = getLocationFromUrl(ctx);

          // qaCookie determines if this is an automated test build
          const qaCookie = ctx?.req?.cookies?.opt_testAutomation ?? false;
          // If build is from test automation, send 'qa' / else send 'environment' variable as is passed
          const env = qaCookie ? 'qa' : environment;
          ctx.data.optimizelyDynamicConfig = {
            dataFile: getOptimizelyDataFile(),
            user: {
              id: userId,
              // NOTE: Any attributes values must be of type 'string', 'boolean', or 'null'
              attributes: {
                brand: ctx?.data?.brand || '',
                // Device name and type come from the Akamai headers
                device_type: ctx?.req?.device?.type ?? null,
                device_name: ctx?.req?.device?.name ?? null,
                // environment can be set as 'prod' or 'qa'
                environment: env,
                experience: ctx?.query?.experience ?? null,
                lnx_code: ctx?.query?.LNX ?? ctx?.query?.lnx ?? ctx?.req?.cookies?.LNX ?? ctx?.req?.cookies?.lnx ?? null,
                // NOTE: $opt_user_agent enables bot filtering
                $opt_user_agent: ctx.req && ctx.req.headers['user-agent'] || 'unknown',
                dma: ctx?.query?.dma?.code ?? null,
                city: (city || ctx?.query?.city) ?? null,
                zipcode: (zip || ctx?.query?.zip) ?? null,
                state: (state || ctx?.query?.state) ?? null,
                urlPath: urlPath || ctx?.req?.originalUrl,
                utm_medium: ctx?.query?.utm_medium ?? null,
                utm_campaign: ctx?.query?.utm_campaign ?? null
              }
            },
            forcedFeatures,
            forcedExperiments
          };
        } else {
          remainingAttempts -= 1;
          if (remainingAttempts) {
            // eslint-disable-next-line no-await-in-loop
            await delayMS(tenSecondsMS);
          } else {
            throw new Error(`Could not initialize Optimizely instance after ${allowedAttempts} attempts.`);
          }
        }
      }
    } catch (e) {
      // Please update the query data in New Relic alert conditions to reflect any changes on this line.
      const errorMessage = 'Could not initialize Optimizely instance. Optimizely experiments and metrics will be missing/wrong. ' + e;
      // eslint-disable-next-line no-console
      console.error(errorMessage);
      onConfigLoadError(errorMessage);
    }
  } else if (ctx.data && !ctx.data.optimizelyDynamicConfig) {
    // bonnet < v10 requires the data to be pulled from BONNET_DATA
    ctx.data.optimizelyDynamicConfig = globalThis?.__BONNET_DATA__?.props?._APP_?.optimizelyDynamicConfig || globalThis?.__BONNET_DATA__?.props?.Connect?._APP_?.optimizelyDynamicConfig || globalThis?.__NEXT_DATA__?.props?.optimizelyDynamicConfig;
  }
};
export default addOptimizelyDynamicConfig;